// lib/rewardFormat.js
// R4 연속 보상 관리자 화면(RewardsView)의 표시 계산. 계약: docs/architecture/app-v2/R6a-R4.md B3
// 날짜는 서울 기준이다. 한국은 서머타임이 없어 UTC+9로 고정 계산한다.
// 이 파일은 import가 없다(scripts/test-rewards.mjs가 node로 그대로 불러 시험한다).

const SEOUL_OFFSET_MS = 9 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
// 시간대 표기가 없는 시각("2026-11-07T15:00:41")은 서울 벽시계로 본다
const NAIVE_DATETIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

// 연속 보상 단계(결정 R4-8: 단계마다 평생 한 번)
export const TIER_WEEKS = [4, 8, 16, 32];

// "YYYY-MM-DD" → { y, m, d }. 형식이 아니면 null
function parseYmd(ymd) {
  const match = YMD_RE.exec(String(ymd || ""));
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const t = Date.UTC(y, m - 1, d);
  const check = new Date(t);
  if (check.getUTCMonth() !== m - 1 || check.getUTCDate() !== d) return null;
  return { y, m, d, t };
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function ymdFromUtcMs(t) {
  const date = new Date(t);
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(
    date.getUTCDate()
  )}`;
}

// 서버 시각 문자열(RFC 3339 UTC "…Z", "+09:00", 시간대 없음)을 순간(ms)으로. 못 읽으면 NaN
export function parseInstant(value) {
  if (value === null || value === undefined || value === "") return NaN;
  if (value instanceof Date) return value.getTime();
  const text = String(value).trim();
  if (YMD_RE.test(text)) return Date.parse(`${text}T00:00:00+09:00`);
  if (NAIVE_DATETIME_RE.test(text)) {
    return Date.parse(`${text.replace(" ", "T")}+09:00`);
  }
  return Date.parse(text);
}

// 날짜 또는 시각 → 서울 날짜 "YYYY-MM-DD". 못 읽으면 ""
export function toSeoulYmd(value) {
  const text = String(value ?? "").trim();
  if (YMD_RE.test(text)) return parseYmd(text) ? text : "";
  const t = parseInstant(value);
  if (!Number.isFinite(t)) return "";
  return ymdFromUtcMs(t + SEOUL_OFFSET_MS);
}

// 지금의 서울 연도(표시에서 올해면 연도를 뺀다)
export function seoulYear(now = Date.now()) {
  const t = now instanceof Date ? now.getTime() : Number(now);
  return new Date(t + SEOUL_OFFSET_MS).getUTCFullYear();
}

// "YYYY-MM-DD"에 n일을 더한다(기간권 보상 미리보기). 못 읽으면 ""
export function addDaysYmd(ymd, days) {
  const parsed = parseYmd(ymd);
  const n = Number(days);
  if (!parsed || !Number.isFinite(n)) return "";
  return ymdFromUtcMs(parsed.t + Math.trunc(n) * DAY_MS);
}

// "4월 12일". 올해가 아니면 "2027년 1월 2일"
export function formatMonthDay(value, refYear) {
  const parsed = parseYmd(toSeoulYmd(value));
  if (!parsed) return "-";
  if (refYear && parsed.y !== refYear) {
    return `${parsed.y}년 ${parsed.m}월 ${parsed.d}일`;
  }
  return `${parsed.m}월 ${parsed.d}일`;
}

// "2026년 11월 4일"
export function formatFullDate(value) {
  const parsed = parseYmd(toSeoulYmd(value));
  if (!parsed) return "-";
  return `${parsed.y}년 ${parsed.m}월 ${parsed.d}일`;
}

// "11월 2일 (월)"
function formatMonthDayWeekday(ymd) {
  const parsed = parseYmd(ymd);
  if (!parsed) return "-";
  const weekday = WEEKDAYS[new Date(parsed.t).getUTCDay()];
  return `${parsed.m}월 ${parsed.d}일 (${weekday})`;
}

// 미션 주(월~토 수업, 결정 R4-1) "11월 2일 (월) - 11월 7일 (토)". weekStart는 월요일
export function formatMissionWeek(weekStart) {
  const start = toSeoulYmd(weekStart);
  if (!parseYmd(start)) return "-";
  return `${formatMonthDayWeekday(start)} - ${formatMonthDayWeekday(
    addDaysYmd(start, 5)
  )}`;
}

// 서울 시각 "2026.11.09 14:12"
export function formatDateTime(value) {
  const t = parseInstant(value);
  if (!Number.isFinite(t)) return "-";
  const date = new Date(t + SEOUL_OFFSET_MS);
  return `${date.getUTCFullYear()}.${pad2(date.getUTCMonth() + 1)}.${pad2(
    date.getUTCDate()
  )} ${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`;
}

// 지급 대기가 생긴 날부터 오늘까지 서울 날짜 차이(일). 못 읽으면 null
export function waitingDays(createdAt, now = Date.now()) {
  const created = parseYmd(toSeoulYmd(createdAt));
  const today = parseYmd(toSeoulYmd(now instanceof Date ? now : new Date(now)));
  if (!created || !today) return null;
  return Math.max(0, Math.round((today.t - created.t) / DAY_MS));
}

// 대기 일수 문구
export function waitingText(createdAt, now = Date.now()) {
  const days = waitingDays(createdAt, now);
  if (days === null) return "-";
  if (days === 0) return "오늘";
  return `${days}일째`;
}

// 보상 종류. 서버 preview.rewardType(count | days)이 정본이고, 없으면 수강권 이름으로 짐작한다
// (D11: Unlimited·주2회권 = 기간권, 10회권·20회권 = 횟수권. 주2회권도 "회권"으로 끝나므로 기간권을 먼저 본다)
export function rewardTypeOf(membership) {
  const type = membership?.preview?.rewardType;
  if (type === "count" || type === "days") return type;
  const name = String(membership?.type || "");
  if (/unlimited|주2회권/i.test(name)) return "days";
  if (/회권/.test(name)) return "count";
  return "";
}

// "+1회" / "+3일"
export function rewardValueText(rewardType, rewardValue) {
  const value = Number(rewardValue);
  if (!Number.isFinite(value)) return "-";
  if (rewardType === "count") return `+${value}회`;
  if (rewardType === "days") return `+${value}일`;
  return `+${value}`;
}

function hasCount(n) {
  return Number.isFinite(Number(n)) && n !== null && n !== "" && Number(n) >= 0;
}

// 수강권 라디오 아래 줄: "남은 6회 · 만료 12월 12일" / "만료 12월 12일"
export function membershipSummary(membership, refYear) {
  const parts = [];
  if (
    rewardTypeOf(membership) === "count" &&
    hasCount(membership?.remainingCount)
  ) {
    parts.push(`남은 ${Number(membership.remainingCount)}회`);
  }
  parts.push(`만료 ${formatMonthDay(membership?.expiredAt, refYear)}`);
  return parts.join(" · ");
}

// 고른 수강권 기준 미리보기(B2): "10회권 남은 6회 → 7회" / "만료일 4월 12일 → 4월 15일"
export function previewText(membership, refYear) {
  if (!membership) return "";
  const rewardType = rewardTypeOf(membership);
  const value = Number(membership?.preview?.rewardValue);
  if (!Number.isFinite(value) || value <= 0) {
    return "적용 값을 계산하지 못했어요";
  }
  if (rewardType === "count") {
    if (!hasCount(membership.remainingCount)) {
      return `${membership.type || "횟수권"} +${value}회`;
    }
    const before = Number(membership.remainingCount);
    return `${membership.type || "횟수권"} 남은 ${before}회 → ${
      before + value
    }회`;
  }
  if (rewardType === "days") {
    const before = toSeoulYmd(membership.expiredAt);
    const after = addDaysYmd(before, value);
    if (!before || !after) return `만료일 +${value}일`;
    return `만료일 ${formatMonthDay(before, refYear)} → ${formatMonthDay(
      after,
      refYear
    )}`;
  }
  return rewardValueText(rewardType, value);
}

// 적용 완료 행의 전·후 값: "남은 6회 → 7회" / "만료일 12월 12일 → 12월 15일"
export function beforeAfterText(applied, refYear) {
  if (!applied) return "-";
  const before = applied.before || {};
  const after = applied.after || {};
  if (applied.rewardType === "count") {
    if (!hasCount(before.remainingCount) || !hasCount(after.remainingCount)) {
      return "-";
    }
    return `남은 ${Number(before.remainingCount)}회 → ${Number(
      after.remainingCount
    )}회`;
  }
  if (applied.rewardType === "days") {
    return `만료일 ${formatMonthDay(
      before.expiredAt,
      refYear
    )} → ${formatMonthDay(after.expiredAt, refYear)}`;
  }
  return "-";
}

// 적용 완료 행의 수강권 이름. 응답에 이름이 없으면 같은 항목의 수강권 목록, 그다음 종류로 표시한다
export function appliedMembershipName(item) {
  const applied = item?.applied;
  if (!applied) return "-";
  const direct = applied.membershipType || applied.type;
  if (direct) return direct;
  const list = Array.isArray(item?.memberships) ? item.memberships : [];
  const found = list.find((m) => m && m.id === applied.membershipId);
  if (found?.type) return found.type;
  if (applied.rewardType === "count") return "횟수권";
  if (applied.rewardType === "days") return "기간권";
  return "-";
}

// 적용 모달의 수강권 순서: 추천을 맨 위, 나머지는 서버 순서 그대로
export function orderMemberships(memberships, suggestedId) {
  const list = Array.isArray(memberships) ? memberships.filter(Boolean) : [];
  const suggested = list.filter((m) => m.id === suggestedId);
  const rest = list.filter((m) => m.id !== suggestedId);
  return [...suggested, ...rest];
}

// 모달을 열 때 처음 고를 수강권: 추천, 없으면 첫 번째, 수강권이 없으면 ""
export function initialMembershipId(item) {
  const list = Array.isArray(item?.memberships) ? item.memberships : [];
  if (!list.length) return "";
  const suggested = list.find((m) => m && m.id === item.suggestedMembershipId);
  return (suggested || list[0]).id || "";
}

// 지급 대기는 오래된 순(createdAt 오름차순), 서버 정렬과 같다. 같은 시각은 서버 순서 유지
export function sortPending(items) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  return list
    .map((item, index) => ({ item, index, t: parseInstant(item.createdAt) }))
    .sort((a, b) => {
      const at = Number.isFinite(a.t) ? a.t : Infinity;
      const bt = Number.isFinite(b.t) ? b.t : Infinity;
      if (at !== bt) return at - bt;
      return a.index - b.index;
    })
    .map((entry) => entry.item);
}

// 적용 완료는 최신 순(applied.appliedAt 내림차순), 서버 정렬과 같다
export function sortApplied(items) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  return list
    .map((item, index) => ({
      item,
      index,
      t: parseInstant(item.applied?.appliedAt),
    }))
    .sort((a, b) => {
      const at = Number.isFinite(a.t) ? a.t : -Infinity;
      const bt = Number.isFinite(b.t) ? b.t : -Infinity;
      if (at !== bt) return bt - at;
      return a.index - b.index;
    })
    .map((entry) => entry.item);
}
